/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { NavigationService } from '@treasury/core/navigation';
import { Feature } from '@treasury/domain/services/feature-flags';
import { TmDetailedError } from '@treasury/domain/shared';
import type { WireCreditorFilter, WireIsoLabels } from '@treasury/domain/wires';
import {
    CreditorsService,
    WireBeneficiary,
    WireCreditor,
    WireIsoService,
} from '@treasury/domain/wires';
import { TmContainer } from '@treasury/presentation';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { HeaderBarService } from '../../../services/jhd-header-bar.service';
import '../partials/creditor-data-readonly';

export const tagName = 'creditor-container';
@customElement(tagName)
export class CreditorContainer extends TmContainer {
    @InjectProperty()
    declare private readonly headerService: HeaderBarService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private creditorsService: CreditorsService;

    @InjectProperty()
    declare private wireIsoService: WireIsoService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @state()
    private creditors: Array<WireBeneficiary> | Array<WireCreditor> = [];

    @state()
    private creditor?: WireBeneficiary | WireCreditor;

    @state()
    private wireIsoLabels: WireIsoLabels = WireIsoService.legacyLabels;

    @state()
    private editCreditorFeatureFlag = false;

    public connectedCallback(): void {
        super.connectedCallback();
        this.loading = true;
    }

    protected render() {
        if (this.loading) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }
        const buttonConfig = [];

        const canApprove =
            this.creditor?.permissions.some(
                (p: { permission: string }) => p.permission === 'Approve'
            ) && this.creditor?.status === 'Pending Approval';

        const canReject =
            this.creditor?.permissions.some(
                (p: { permission: string }) => p.permission === 'Reject'
            ) && this.creditor?.status === 'Pending Approval';

        const canInitiate =
            this.creditor?.permissions.some(
                (p: { permission: string }) => p.permission === 'CreateWire'
            ) && this.creditor?.status === 'Ready';

        const hasEditableStatus = ['Ready', 'Rejected'].includes(this.creditor?.status as string);
        const canEdit =
            this.creditor?.permissions.some(
                (p: { permission: string }) => p.permission === 'Edit'
            ) && hasEditableStatus;

        const canDelete =
            this.creditor?.permissions.some(
                (p: { permission: string }) => p.permission === 'Delete'
            ) && this.creditor?.status === 'Rejected';

        if (canApprove) {
            buttonConfig.push({
                text: 'Approve',
                success: true,
                disabled: false,
                onClick: () => {
                    this.approveCreditor(this.creditor as WireCreditor);
                },
            });
        }

        if (canReject) {
            buttonConfig.push({
                text: 'Reject',
                error: true,
                disabled: false,
                onClick: () => {
                    this.rejectCreditor(this.creditor as WireCreditor);
                },
            });
        }

        if (canInitiate) {
            buttonConfig.push({
                text: 'Initiate',
                disabled: false,
                onClick: () => {
                    this.navService.navigate(
                        `/wires/${this.creditor?.isInternational ? 'international' : 'domestic'}/create/${this.creditor?.id}/usd`
                    );
                },
            });
        }

        if (canEdit && this.editCreditorFeatureFlag) {
            buttonConfig.push({
                text: 'Edit',
                disabled: false,
                importance: 'secondary',
                onClick: () => {
                    this.navService.navigate(`/wires/creditors/edit/${this.creditor?.id}`);
                },
            });
        }

        if (canDelete) {
            buttonConfig.push({
                error: true,
                text: 'Delete',
                disabled: false,
                onClick: () => {
                    this.deleteCreditor();
                },
            });
        }

        return html`<tm-body .padding=${false}>
            <creditor-data-readonly .creditor=${this.creditor}></creditor-data-readonly>
            <tm-footer .buttonConfig=${buttonConfig}></tm-footer>
        </tm-body>`;
    }

    protected async firstUpdated() {
        const { params } = await this.navService.getRouteData<{ id: string }>();

        this.editCreditorFeatureFlag = await (
            await this.featureFlagService
        ).isEnabled(Feature.PwaWireEditCreditor);

        this.creditors = await this.creditorsService.getAll({
            requireEntitlement: true,
            beneficiaryType: 'Both',
            status: 'All',
            beneficiaryName: '',
            accountNumber: '',
            bankId: '',
            bankName: '',
            country: '',
            createdDateType: 'Specific Date',
            createdDateStart: '',
            createdDateEnd: '',
            createdDate: '',
        } as WireCreditorFilter);
        this.creditor = this.creditors.find(creditor => creditor.id === Number(params.id));
        this.wireIsoLabels = await this.wireIsoService.getLabels();
        this.loading = false;

        this.headerService.configure({
            title: `${this.wireIsoLabels.creditor} Detail`,
            backAction: () => this.navService.navigate('/wires/creditors'),
        });
    }

    private async deleteCreditor() {
        const { reason } = await this.dialogService.open(
            `Are you sure you want to delete this Creditor?`,
            'Confirm Deletion',
            {
                buttons: {
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Delete',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                },
                classes: ['max-w-[500px]'],
            }
        );
        if (reason === DynamicComponentExitReason.Confirm) {
            this.loading = true;
            try {
                await this.creditorsService.delete(this.creditor?.id as number);
                this.navService.navigate('/wires/creditors');
            } catch (error) {
                if (error instanceof TmDetailedError) {
                    this.notificationService.renderError(error);
                } else if (error instanceof Error) {
                    this.notificationService.renderError(error);
                } else {
                    this.notificationService.renderError('Error processing your wire');
                }
            } finally {
                this.loading = false;
            }
        }
        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
    }

    private async approveCreditor(creditor: WireCreditor) {
        const { reason } = await this.dialogService.open(
            `Are you sure you want to approve this creditor?`,
            'Confirm Approval',
            {
                buttons: {
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Approve',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                },
                classes: ['max-w-[500px]'],
            }
        );
        if (reason === DynamicComponentExitReason.Confirm) {
            this.loading = true;
            try {
                await this.creditorsService.approve(creditor.id as number, '');
                //    dispatch correct event to refetch creditors list
            } catch (error) {
                if (error instanceof TmDetailedError) {
                    this.notificationService.renderError(error);
                } else if (error instanceof Error) {
                    this.notificationService.renderError(error);
                } else {
                    this.notificationService.renderError('Error processing your wire');
                }
            } finally {
                this.loading = false;
            }
        }
        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
    }

    private async rejectCreditor(creditor: WireCreditor) {
        const { reason } = await this.dialogService.open(
            `Are you sure you want to reject this creditor?`,
            'Confirm Rejection',
            {
                buttons: {
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Reject',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                },
                classes: ['max-w-[500px]'],
            }
        );
        if (reason === DynamicComponentExitReason.Confirm) {
            this.loading = true;
            try {
                await this.creditorsService.reject(creditor.id, '');
                //    dispatch correct event to refetch creditors list
            } catch (error) {
                if (error instanceof TmDetailedError) {
                    this.notificationService.renderError(error);
                } else if (error instanceof Error) {
                    this.notificationService.renderError(error);
                } else {
                    this.notificationService.renderError('Error processing your wire');
                }
            } finally {
                this.loading = false;
            }
        }
        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CreditorContainer;
    }
}
